import { useFragment } from "@/generated";
import { MoveSlidesDocument, SlideFieldsFragmentDoc } from "@/generated/graphql";
import { saveScrollTarget } from "@/lib/utils/scrollRestoration";
import { withToast } from "@/lib/utils/withToast";
import type { Reference } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { useNavigate } from "@tanstack/react-router";

/**
 * Hook for moving slides to a different lesson.
 *
 *
 * @example
 * const { moveSlides, loading } = useMoveSlides(currentLessonId);
 * await moveSlides(["slide1", "slide2"], "targetLesson123");
 */
export function useMoveSlides() {
  const [moveSlidesMutation, { loading }] = useMutation(MoveSlidesDocument, {
    update(cache, { data }, { context, variables }) {
      if (!data?.moveSlides) return;

      try {
        const sourceLessonId = context?.sourceLessonId;
        const targetLessonId = variables?.targetLessonId;

        if (!sourceLessonId) throw new Error();

        const slides = data?.moveSlides.map((slide) => useFragment(SlideFieldsFragmentDoc, slide));

        if (!slides) throw new Error();

        cache.batch({
          update(cache) {
            const ids: string[] = [];

            for (const slide of slides) {
              const id = cache.identify(slide);
              if (id) {
                cache.writeFragment({
                  id,
                  fragment: SlideFieldsFragmentDoc,
                  data: slide,
                });
                ids.push(id);
              }
            }

            const newRefs = ids.map((id) => ({ __ref: id }));

            // Add slide to target lesson cache
            cache.modify({
              id: "ROOT_QUERY",
              fields: {
                slidesByLessonId(existingData, { storeFieldName }) {
                  const key = `"lessonId":"${targetLessonId}"`;
                  const isTrashKey = '"isTrash":false';

                  if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                    return existingData;

                  if (!existingData?.items) return existingData;

                  return {
                    ...existingData,
                    items: [...newRefs, ...existingData.items],
                    total: (existingData.total || 0) + newRefs.length,
                  };
                },
              },
            });

            // Remove slide from source lesson cache
            cache.modify({
              id: "ROOT_QUERY",
              fields: {
                slidesByLessonId(existingData, { storeFieldName, readField }) {
                  const key = `"lessonId":"${sourceLessonId}"`;
                  const isTrashKey = '"isTrash":false';

                  if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                    return existingData;

                  if (!existingData?.items) return existingData;

                  return {
                    ...existingData,
                    items: existingData.items.filter(
                      (ref: Reference) => !slides.some((s) => readField("id", ref) === s.id)
                    ),
                    total: (existingData.total || 0) - newRefs.length,
                  };
                },
              },
            });
          },
        });
      } catch (error) {
        console.error("Move slide cache update failed:", error);
      }
    },
  });

  const navigate = useNavigate();

  const moveSlides = async (slideIds: string[], targetLessonId: string, sourceLessonId: string) => {
    return withToast(
      async () => {
        const result = await moveSlidesMutation({
          variables: {
            slideIds,
            targetLessonId,
          },
          context: {
            sourceLessonId,
          },
        });

        const maskedSlides = result.data?.moveSlides ?? [];
        const movedSlides = maskedSlides.map((slide) => useFragment(SlideFieldsFragmentDoc, slide));

        // Save scroll target for the first moved slide
        if (slideIds.length > 0) {
          saveScrollTarget(`slide-${slideIds[0]}`, targetLessonId);
        }

        navigate({
          to: "/admin/lesson/$lessonId",
          params: { lessonId: targetLessonId },
        });

        return movedSlides;
      },
      {
        loading: `Moving ${slideIds.length} slide${slideIds.length !== 1 ? "s" : ""}...`,
        success: `Successfully moved ${slideIds.length} slide${slideIds.length !== 1 ? "s" : ""}`,
        error: "Failed to move slides. Please try again.",
      },
      "moveSlides"
    );
  };

  return {
    moveSlides,
    loading,
  };
}
